import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@urc-ui/store/types';
import {
  reportMetadataApi,
  selectCustomReportMetadataResult,
  selectReportMetadataResult
} from '@urc-ui/store/api/report-metadata/reportMetadata';
import { REPORT_CATEGORY_GROUPS } from '@urc-ui/utils/subscription-utils';
import { getReportMetadataOptions, ReportMetadataOptions } from '@urc-ui/utils/report-metadata-utils';
import { ReportPeriodState, SetReportPeriodOptionsPayload } from './types';
import { REPORT_CATEGORY, SUBSCRIPTION_FREQUENCY } from '@urc-ui/utils/types';
import { Subscription, SubscriptionDelivery } from '@urc-ui/store/api/subscriptions/types';
import { FacetEnum, ReportMetadata, ReportType } from '@urc-ui/store/api/report-metadata/types';
import { GENERAL_REPORT_TO_RMS_REPORT_TYPE_MAP, RDD_REPORT_TYPES, REPORT_TYPE } from '@urc-ui/utils/report-type-utils';
import { formatInTimeZone } from 'date-fns-tz';
import { ISO_DATE_FORMAT } from '@urc-ui/components/date-input';
import { toDate } from 'date-fns';
import { buildZonedTimeSubscriptionDelivery } from '@urc-ui/store/api/subscriptions/subscriptions';
import { getMarketplaceIdFromContext, getReportPermissions, isRDDAdvertiser } from '@urc-ui/utils/app-utils';

interface FormErrorStateType {
  readonly reportNameError: boolean;
  readonly reportDeliveryError: boolean;
  readonly countrySelectionError: boolean;
}

export interface CreateSubscriptionState {
  readonly reportCategory: string | null;
  readonly reportType: string | null;
  readonly timeUnit: string | null;
  readonly reportPeriod: ReportPeriodState | null;
  readonly reportName: string | undefined;
  readonly defaultReportName: string | undefined;
  readonly subscriptionDelivery: SubscriptionDelivery;
  readonly recipients: string[] | null;
  readonly columns: string[] | null;
  readonly covPickerChecked?: boolean | null;
  readonly mtaPickerChecked?: boolean | null;
  readonly amazonBusinessPickerChecked?: boolean | null;
  readonly mrcPickerChecked?: boolean | null;
  readonly currencyOfVisualization?: string | null;
  readonly marketplaceIds: string[] | null;
  readonly mrcAccreditationStatus?: string | null;
  readonly formErrorState: FormErrorStateType;
}

const defaultMarketplaceId = getMarketplaceIdFromContext();

const initialState: CreateSubscriptionState = {
  reportCategory: null,
  reportType: null,
  timeUnit: null,
  reportPeriod: null,
  reportName: undefined,
  defaultReportName: undefined,
  subscriptionDelivery: {
    frequency: SUBSCRIPTION_FREQUENCY.SINGLE,
    scheduledEndDate: undefined
  },
  recipients: null,
  columns: null,
  mtaPickerChecked: null,
  amazonBusinessPickerChecked: null,
  covPickerChecked: null,
  currencyOfVisualization: null,
  marketplaceIds: [defaultMarketplaceId],
  mrcAccreditationStatus: null,
  formErrorState: {
    reportNameError: false,
    reportDeliveryError: false,
    countrySelectionError: false
  }
};

const getDefaultReportConfigOptions = (reportMetadata: ReportMetadata) => {
  const orderedReportCategories = REPORT_CATEGORY_GROUPS.flat();
  const defaultCategoryId =
    isRDDAdvertiser() && reportMetadata.reportCategories?.[REPORT_CATEGORY.SP]
      ? REPORT_CATEGORY.SP
      : reportMetadata.reportCategories && orderedReportCategories.find(categoryId => !!reportMetadata.reportCategories[categoryId]);
  if (defaultCategoryId) {
    const { reportCategory, reportType, timeUnit } = getReportMetadataOptions(reportMetadata, defaultCategoryId);
    return {
      reportCategory,
      reportType,
      timeUnit
    };
  }
  return {
    reportCategory: null,
    reportType: null,
    timeUnit: null
  };
};

const CreateSubscriptionSlice = createSlice({
  name: 'CreateSubscriptionSlice',
  initialState,
  reducers: {
    setReportCategoryOptions: (state, action: PayloadAction<ReportMetadataOptions>) => {
      const { reportCategory, reportType, timeUnit } = action.payload;
      state.reportCategory = reportCategory;
      state.reportType = reportType;
      state.timeUnit = timeUnit;
      state.marketplaceIds = [defaultMarketplaceId];
      state.mtaPickerChecked = null;
      state.amazonBusinessPickerChecked = null;
      state.covPickerChecked = null;
      state.currencyOfVisualization = null;
    },
    setReportTypeOptions: (state, action: PayloadAction<{ reportType: string; timeUnit: string }>) => {
      const { reportType, timeUnit } = action.payload;
      state.reportType = reportType;
      state.timeUnit = timeUnit;
      state.marketplaceIds = [defaultMarketplaceId];
      state.amazonBusinessPickerChecked = null;
      state.covPickerChecked = null;
      state.currencyOfVisualization = null;
    },
    setReportPeriodOptions: (state, action: SetReportPeriodOptionsPayload) => {
      state.reportPeriod = action.payload;
    },
    setTimeUnit: (state, action: PayloadAction<{ timeUnit: string }>) => {
      const { timeUnit } = action.payload;
      state.timeUnit = timeUnit;
    },
    setReportName: (state, action: PayloadAction<{ updatedReportName: string; reportNameError?: boolean }>) => {
      const { updatedReportName, reportNameError } = action.payload;
      state.reportName = updatedReportName;
      state.defaultReportName = undefined;
      state.formErrorState.reportNameError = !!reportNameError;
    },
    setSubscriptionDelivery: (
      state,
      action: PayloadAction<{ subscriptionDelivery: SubscriptionDelivery; reportDeliveryError?: boolean }>
    ) => {
      const { subscriptionDelivery, reportDeliveryError } = action.payload;
      state.subscriptionDelivery = subscriptionDelivery;
      state.formErrorState.reportDeliveryError = !!reportDeliveryError;
    },
    setRecipients: (state, action: PayloadAction<string[]>) => {
      state.recipients = action.payload;
    },
    copySubscription: (state, action: PayloadAction<Subscription & { timezone: string }>) => {
      const payload = action.payload;
      state.reportCategory = payload.programType === 'hsa' || payload.programType === 'sbv' ? 'sb' : payload.programType;
      state.reportType = GENERAL_REPORT_TO_RMS_REPORT_TYPE_MAP[payload.reportType as REPORT_TYPE] || payload.reportType;
      /**
       * Carbyne stores data in day granularity in marketplace timezone. for example, 2023-05-05 has fact data from 2023-05-05 PST start of the day
       * to 2023-05-05 PST end of the day.
       *
       * report period timestamps are stored in Corvo as start of the day UTC. for example, if user selected 5/5/2023 to 5/25/2023
       * as report period in UI in US marketpalce, Corvo stores the startDate as 2023-05-05 12:00AM UTC and end date as 2023-05-25 12:00AM UTC.
       * But Corvo queries Carbyne with just 2023-05-05 to 2023-05-25.
       *
       * For this reason, the timestamps from corvo should be parsed as UTC timestamp here, but the dates represent marketplace dates.
       */
      state.reportPeriod = {
        period: payload.reportPeriod?.toUpperCase(),
        start: payload.reportStartDate ? formatInTimeZone(toDate(payload.reportStartDate), 'UTC', ISO_DATE_FORMAT) : null,
        end: payload.reportEndDate ? formatInTimeZone(toDate(payload.reportEndDate), 'UTC', ISO_DATE_FORMAT) : null
      };
      state.timeUnit = payload.timeUnit;
      state.subscriptionDelivery = buildZonedTimeSubscriptionDelivery(payload.subscriptionDelivery, payload.timezone);
      state.defaultReportName = payload.name;
      state.recipients = payload.recipients;
      state.covPickerChecked = !!payload.currencyOfVisualization;
      state.mtaPickerChecked = !!payload.hasMultiTouchAttribution;
      state.currencyOfVisualization = payload.currencyOfVisualization;
      state.marketplaceIds = payload.marketplaceIds;
      state.amazonBusinessPickerChecked = !!payload.isAmazonBusinessPickerChecked;
    },
    setMTAPickerChecked: (state, action: PayloadAction<boolean | null>) => {
      state.mtaPickerChecked = action.payload;
    },
    setAmazonBusinessPickerChecked: (state, action: PayloadAction<boolean | null>) => {
      state.amazonBusinessPickerChecked = action.payload;
    },
    setMRCPickerChecked: (state, action: PayloadAction<boolean | null>) => {
      state.mrcPickerChecked = action.payload;
    },
    setCoVPickerChecked: (state, action: PayloadAction<boolean | null>) => {
      state.covPickerChecked = action.payload;
    },
    setCurrencyOfVisualization: (state, action: PayloadAction<string | null>) => {
      state.currencyOfVisualization = action.payload;
    },
    resetCreateReportForm: (state, action) => {
      state = {
        ...initialState,
        ...getDefaultReportConfigOptions(action.payload)
      };
      return state;
    },
    setMarketplaceIds: (state, action: PayloadAction<{ marketplaceIds: string[]; countrySelectionError: boolean }>) => {
      const { marketplaceIds, countrySelectionError } = action.payload;
      state.marketplaceIds = marketplaceIds;
      state.formErrorState.countrySelectionError = !!countrySelectionError;
    },
    setMRCStatus: (state, action: PayloadAction<{ status: string | null }>) => {
      const { status } = action.payload;
      state.mrcAccreditationStatus = status;
    }
  },
  extraReducers: builder => {
    builder.addMatcher(reportMetadataApi.endpoints.getReportMetadata.matchFulfilled, (state, action) => {
      const { reportCategory, reportType, timeUnit } = getDefaultReportConfigOptions(action.payload);
      state.reportCategory = reportCategory;
      state.reportType = reportType;
      state.timeUnit = timeUnit;
    });
  }
});

export const {
  setReportCategoryOptions,
  setReportTypeOptions,
  setTimeUnit,
  setReportPeriodOptions,
  setReportName,
  setSubscriptionDelivery,
  setRecipients,
  copySubscription,
  resetCreateReportForm,
  setCoVPickerChecked,
  setMTAPickerChecked,
  setAmazonBusinessPickerChecked,
  setMRCPickerChecked,
  setCurrencyOfVisualization,
  setMarketplaceIds,
  setMRCStatus
} = CreateSubscriptionSlice.actions;
export const getReportCategory = (state: RootState) => state.createSubscription.reportCategory;
export const getReportType = (state: RootState) => state.createSubscription.reportType;
export const selectReportPeriod = (state: RootState) => state.createSubscription.reportPeriod;
export const selectReportName = (state: RootState) => state.createSubscription.reportName;
export const selectDefaultReportName = (state: RootState) => state.createSubscription.defaultReportName;
export const selectSubscriptionDelivery = (state: RootState) => state.createSubscription.subscriptionDelivery;
export const selectRecipients = (state: RootState) => state.createSubscription.recipients;
export const getTimeUnit = (state: RootState) => state.createSubscription.timeUnit;
export const getShowCoVPicker = (state: RootState) => state.createSubscription.covPickerChecked;
export const getShowMTAPicker = (state: RootState) => state.createSubscription.mtaPickerChecked;
export const getAmazonBusinessPickerChecked = (state: RootState) => state.createSubscription.amazonBusinessPickerChecked;
export const getMRCPickerChecked = (state: RootState) => state.createSubscription.mrcPickerChecked;
export const getCurrencyOfVisualization = (state: RootState) => state.createSubscription.currencyOfVisualization;
export const getCreateSubscriptionData = (state: RootState) => state.createSubscription;
export const getFormErrorState = (state: RootState) => state.createSubscription.formErrorState;

const getCustomReportMetadataData = (state: RootState) =>
  selectCustomReportMetadataResult({
    reportCategory: state.createSubscription.reportCategory as string,
    reportType: state.createSubscription.reportType as string
  })(state);

export const getCustomReportType = createSelector(getCustomReportMetadataData, customReportMetadata => {
  if (!customReportMetadata.data) {
    return null;
  }
  return customReportMetadata.data.customReportType;
});

export const selectCustomReportColumns = createSelector(getCustomReportMetadataData, customReportMetadata => {
  if (!customReportMetadata.data) {
    return [];
  }
  return customReportMetadata.data.customReportType.columns || [];
});
export const getCurrentSelectedMarketplaces = (state: RootState) => state.createSubscription.marketplaceIds;

export const selectReportConfig = createSelector(
  selectReportMetadataResult,
  getReportCategory,
  getReportType,
  (reportMetadata, reportCategory, reportType) => {
    if (!reportMetadata.data || !reportCategory || !reportType) {
      return {} as ReportType;
    }
    return reportMetadata.data.reportCategories[reportCategory].reportTypeList.find(item => item.id === reportType);
  }
);

export const selectIsCustomReport = createSelector(selectReportConfig, selectedReportConfig => !!selectedReportConfig?.isCustomReport);

export const selectAvailableTimeUnits = createSelector(
  selectReportMetadataResult,
  getCustomReportMetadataData,
  selectIsCustomReport,
  getReportCategory,
  getReportType,
  (reportMetadata, customReportMetadata, isCustomReport, reportCategory, reportType) => {
    if (isCustomReport && customReportMetadata.data) {
      return customReportMetadata.data.customReportType.facets.find(facet => facet.id === FacetEnum.TIME_UNIT)?.options || [];
    }
    if (!reportMetadata.data || !reportCategory || !reportType) {
      return [];
    }
    return reportMetadata.data.reportCategories[reportCategory].reportTypeList.find(item => item.id === reportType)?.timeUnits || [];
  }
);

export const selectAvailableReportTypes = createSelector(
  selectReportMetadataResult,
  getReportCategory,
  (reportMetadata, reportCategory) => {
    if (!reportMetadata.data || !reportCategory) {
      return [];
    }
    // filter out the report types that are not available for the user
    const disabledReportTypesByCategory = (getReportPermissions()?.disabledReportTypes as Record<string, string[]>) || {};
    const disabledReportTypes = disabledReportTypesByCategory[reportCategory] || [];

    return reportMetadata.data.reportCategories[reportCategory].reportTypeList
      .filter(reportType => !disabledReportTypes.includes(reportType.id))
      .filter(reportType => !isRDDAdvertiser() || RDD_REPORT_TYPES.includes(reportType.id))
      .map(reportType => ({
        id: reportType.id,
        name: reportType.name
      }));
  }
);

export const selectAvailableReportCategories = createSelector(selectReportMetadataResult, reportMetadata => {
  if (!reportMetadata.data) {
    return [];
  }

  // filter out the categories that are not available for the user
  const disabledReportCategories = (getReportPermissions()?.disabledReportCategories as string[]) || [];

  const reportCategories = Object.keys(reportMetadata.data.reportCategories)
    .filter(categoryId => !disabledReportCategories.includes(categoryId))
    .filter(categoryId => !isRDDAdvertiser() || categoryId === REPORT_CATEGORY.SP)
    .reduce((result: Record<string, Record<string, string>>, categoryId) => {
      const reportCategory = reportMetadata.data?.reportCategories[categoryId];
      result[categoryId] = {
        id: reportCategory?.id || '',
        name: reportCategory?.name || ''
      };
      return result;
    }, {});

  const availableGroups = REPORT_CATEGORY_GROUPS.map(group => group.filter(categoryId => !!reportCategories[categoryId]));
  return availableGroups.map(group => group.map(categoryId => reportCategories[categoryId])).filter(group => group.length > 0);
});

export type { ReportPeriodState };

export default CreateSubscriptionSlice.reducer;
